import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import GroupsList from "../../components/groups-list/groups-list";
import GroupDetail from "../../components/group-detail/group-detail";
import NotificationsPanel from "../../components/notifications-panel/notifications-panel";
import CreateGroupModal from "../../components/create-group-modal/create-group-modal";
import AddMemberModal from "../../components/add-member-modal/add-member-modal";
import ComposeEmailModal from "../../components/compose-email-modal/compose-email-modal";
import EditGroupModal from "../../components/edit-group-modal/edit-group-modal";
import OrganizationSettingsModal from "../../components/organization-settings-modal/organization-settings-modal";
import { useGroups, useAlerts, useOrganizationSettings, useGroupParticipants } from "../../hooks/useCoralApi";
import coralService from "../../../../services/coral/coral-service";
import DeleteGroupModal from "../../components/delete-group-modal/delete-group-modal";
import "./whatsapp-groups-page.scss";
import Modal from "../../../../shared/components/dashboard-layout/modalComponent";

const WhatsAppGroupsPage = () => {
  // UI State
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetails, setGroupDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isGroupSwitching, setIsGroupSwitching] = useState(false);
  const [groupFilter, setGroupFilter] = useState("all"); // "all", "standard", "anonymous"
  
  // Modal States
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showOrganizationSettingsModal, setShowOrganizationSettingsModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showComposeEmailModal, setShowComposeEmailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalHeaderAndMessage, setSuccessModalHeaderAndMessage] = useState({
    header: "",
    message: ""
  });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalHeaderAndMessage, setErrorModalHeaderAndMessage] = useState({
    header: "",
    message: ""
  });

  // Notifications State
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Local overrides so edited names/descriptions persist in list even after selection changes
  const [groupOverrides, setGroupOverrides] = useState({});
  // Optimistic unread resets per group id
  const [optimisticReadIds, setOptimisticReadIds] = useState({});

  // API Hooks
  const {
    groups,
    loading: groupsLoading,
    editLoading: editGroupLoading,
    createLoading: createGroupLoading,
    refetch: refetchGroups,
    createGroup,
    editGroup
  } = useGroups(groupFilter);
  const { alerts, loading: alertsLoading, sendAlertEmail } = useAlerts();
  const { organizationSettings, loading: organizationSettingsLoading, updateOrganizationSettings } = useOrganizationSettings();
  const {
    participants,
    loading: participantsLoading,
    addParticipant,
    removeParticipant,
    refetch: refetchParticipants,
    error: participantsError
  } = useGroupParticipants(selectedGroup?.id);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  // Group Selection Handler
  const handleGroupSelect = async (group) => {
    setIsGroupSwitching(true);
    setShowMembersModal(false);
    setShowNotifications(false);
    
    // Clear messages from the group object to prevent showing stale messages
    const groupWithoutMessages = group ? { ...group, messages: [] } : null;
    setSelectedGroup(groupWithoutMessages);
    
    // Clear previous group details immediately to prevent stale data
    setGroupDetails(null);

    if (group?.id) {
      // Optimistically mark as read in UI for the list
      setOptimisticReadIds((prev) => ({ ...prev, [group.id]: true }));
      setLoadingDetails(true);
      try {
        const details = await coralService.getGroupWithDetails(organizationId, group.id);
        setGroupDetails(details);
        // Rely on optimistic mask + websocket/server updates to sync unread count
      } catch (error) {
        console.error('Failed to fetch group details:', error);
        setGroupDetails(null);
      } finally {
        setLoadingDetails(false);
        setTimeout(() => setIsGroupSwitching(false), 300);
      }
    } else {
      setTimeout(() => setIsGroupSwitching(false), 300);
    }
  };

  // When server/websocket updates show unread > 0 for any group, clear optimistic flag so server value is used
  useEffect(() => {
    if (!Array.isArray(groups) || !groups.length) return;
    setOptimisticReadIds((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const g of groups) {
        if (next[g.id] && (typeof g.unreadCount === 'number') && g.unreadCount > 0) {
          delete next[g.id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [groups]);

  const handleConfirmDeleteGroup = async () => {
    if (!selectedGroup?.id) return;
    try {
      await coralService.deleteGroup(organizationId, selectedGroup.id);
      setShowDeleteModal(false);
      setShowEditModal(false);
      setSelectedGroup(null);
      setGroupDetails(null);
      await refetchGroups();
      setShowSuccessModal(true);
      setSuccessModalHeaderAndMessage({
        header: "Success",
        message: "Group deleted successfully!"
      });
    } catch (error) {
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to delete group: " + error.message
      });
      throw error;
    }
  };

  // Group Management Handlers
  const handleRefreshGroups = async () => {
    try {
      await refetchGroups();
    } catch (error) {
      console.error('Failed to refresh groups:', error);
    }
  };

  const handleCreateGroup = async (groupData) => {
    try {
      await createGroup(groupData);
      setShowCreateGroupModal(false);
      refetchGroups();
    } catch (error) {
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to create group: " + error.message
      });
    }
  };

  const handleEditGroup = async (name, description, emailRecipients, emailOnEachMessage) => {
    try {
      const updated = await editGroup(selectedGroup.id, { name, description, emailRecipients, emailOnEachMessage });

      // Optimistically update local state so UI reflects immediately
      setSelectedGroup((prev) =>
        prev ? { ...prev,
          name: updated?.name || name,
          description: updated?.description ?? description,
          emailRecipients: updated?.emailRecipients ?? emailRecipients,
          emailOnEachMessage: updated?.emailOnEachMessage ?? emailOnEachMessage
        } : prev
      );
      setGroupDetails((prev) =>
        prev ? { ...prev,
          name: updated?.name || name,
          description: updated?.description ?? description,
          emailRecipients: updated?.emailRecipients ?? emailRecipients,
          emailOnEachMessage: updated?.emailOnEachMessage ?? emailOnEachMessage
        } : prev
      );

      // Persist override for the groups list regardless of current selection
      setGroupOverrides((prev) => ({
        ...prev,
        [selectedGroup.id]: {
          name: updated?.name || name,
          description: updated?.description ?? description,
          emailRecipients: updated?.emailRecipients ?? emailRecipients,
          emailOnEachMessage: updated?.emailOnEachMessage ?? emailOnEachMessage
        },
      }));

      setShowEditModal(false);
      setShowSuccessModal(true);
      setSuccessModalHeaderAndMessage({
        header: "Success",
        message: "Group edited successfully!"
      });
    } catch (error) {
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to edit group: " + error.message
      });
    }
  };

  const handleAddMember = async (memberData) => {
    if (!selectedGroup) return;
    
    try {
      await addParticipant(memberData);
      const details = await coralService.getGroupWithDetails(organizationId, selectedGroup.id);
      setGroupDetails(details);
      setShowAddMemberModal(false);
    } catch (error) {
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to add member: " + error.message
      });
    }
  };

  const handleRemoveMember = async ({ phone }) => {
    if (!selectedGroup) return;

    try {
      await removeParticipant(phone);
      const details = await coralService.getGroupWithDetails(organizationId, selectedGroup.id);
      setGroupDetails(details);
      setShowAddMemberModal(false);
    } catch (error) {
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to remove member: " + error.message
      });
    }
  };

  // Notification Handlers
  const handleShowNotifications = () => {
    setShowNotifications(true);
    setSelectedGroup(null);
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setShowComposeEmailModal(true);
  };

  const handleSendEmail = async (alertId, recipientEmail, subject, body) => {
    try {
      await sendAlertEmail(organizationId, alertId, recipientEmail, subject, body);
      setShowComposeEmailModal(false);
      setSelectedNotification(null);
      setShowSuccessModal(true);
      setSuccessModalHeaderAndMessage({
        header: "Success",
        message: "Email sent successfully!"
      });
    } catch (error) {
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to send email: " + error.message
      });
    }
  };

  const handleUpdateOrganizationSettings = async (organizationSettings) => {
    try {
      await updateOrganizationSettings(organizationSettings);
      setSuccessModalHeaderAndMessage({
        header: "Success",
        message: "Organization settings updated successfully!"
      });
      setShowSuccessModal(true);
      setShowOrganizationSettingsModal(false);
    } catch (error) {
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to update organization settings: " + error.message
      });
    }
  };

  const handleGroupFilterChange = (filter) => {
    if (filter === groupFilter) {
      setGroupFilter("all");
    } else {
      setGroupFilter(filter);
    }
  };

  // Modal Close Handlers
  const closeComposeEmail = () => {
    setShowComposeEmailModal(false);
    setSelectedNotification(null);
  };

  // Render Methods
  const renderMainContent = () => {
    if (showNotifications) {
      return (
        <NotificationsPanel
          notifications={alerts}
          onNotificationClick={handleNotificationClick}
          loading={alertsLoading}
        />
      );
    }

    if (selectedGroup) {
      const liveGroupFromList = groups.find(g => g.id === selectedGroup.id); // necessary for state updates inside GroupDetail
      const groupForDetail = liveGroupFromList || groupDetails || selectedGroup;
      return (
        <GroupDetail
          group={groupForDetail}
          onAddMember={() => setShowAddMemberModal(true)}
          onRemoveMember={handleRemoveMember}
          participants={participants}
          participantsLoading={participantsLoading}
          onRefreshMembers={refetchParticipants}
          loading={loadingDetails}
          isGroupSwitching={isGroupSwitching}
          showMembersModal={showMembersModal}
          setShowMembersModal={setShowMembersModal}
          setShowEditModal={setShowEditModal}
          setShowDeleteModal={setShowDeleteModal}
          organizationSettings={organizationSettings}
          genericModals={{ setShowSuccessModal, setSuccessModalHeaderAndMessage, setShowErrorModal, setErrorModalHeaderAndMessage }}
        />
      );
    }

    return (
      <div className="chat-window">
        <div className="chat-window-inner">
          <div className="chat-window-center">
            <div className="chat-window-center-inner">
              <div className="chat-window-center-inner-content">
                <div className="chat-window-center-inner-content-inner">
                  <img
                    src="assets/images/start-chat.png"
                    alt="chat"
                    className="img-fluid"
                  />
                  <h4 className="mt-3">Select a group to view messages and members</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Ensure the group list reflects any edited name/description immediately, even after switching selection
  // and apply optimistic unread resets (0) until server pushes a newer value
  const enhancedGroups = Array.isArray(groups)
    ? groups.map((g) => {
        const override = groupOverrides[g.id];
        const base = override ? { ...g, ...override } : g;
        if (optimisticReadIds[g.id]) {
          return { ...base, unreadCount: 0 };
        }
        return base;
      })
    : groups;

  return (
    <div className="row chatPageWrapper">
      <div className="col-12 chatPageWrapperInner">
        <div className="chat-box-left">
          <GroupsList
            groups={enhancedGroups}
            selectedGroup={selectedGroup}
            onGroupSelect={handleGroupSelect}
            onCreateGroup={() => setShowCreateGroupModal(true)}
            onOrganizationSettings={() => setShowOrganizationSettingsModal(true)}
            onShowNotifications={handleShowNotifications}
            onRefreshGroups={handleRefreshGroups}
            notificationCount={alerts?.length || 0}
            loading={groupsLoading}
            filter={groupFilter}
            onFilterChange={handleGroupFilterChange}
          />
        </div>
        <div className="chat-box-right">
          {renderMainContent()}
        </div>
      </div>

      {/* Modals */}
      {showCreateGroupModal && (
        <CreateGroupModal
          onClose={() => setShowCreateGroupModal(false)}
          onCreateGroup={handleCreateGroup}
          isLoading={createGroupLoading}
        />
      )}

      {showAddMemberModal && (
        <AddMemberModal
          onClose={() => setShowAddMemberModal(false)}
          onAddMember={handleAddMember}
          isLoading={participantsLoading}
          groupName={selectedGroup?.name}
          error={participantsError}
        />
      )}

      <ComposeEmailModal
        isOpen={showComposeEmailModal}
        onClose={closeComposeEmail}
        notification={selectedNotification}
        onSendEmail={handleSendEmail}
        errorModal={{ setShowErrorModal, setErrorModalHeaderAndMessage }}
      />

      {/* Edit Group Modal */}
      {showEditModal && (
        <EditGroupModal
          group={selectedGroup}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUpdateGroup={handleEditGroup}
          isLoading={editGroupLoading}
        />
      )}

      {/* Organization Settings Modal */}
      {showOrganizationSettingsModal && (
        <OrganizationSettingsModal
          organizationSettings={organizationSettings}
          isOpen={showOrganizationSettingsModal}
          onClose={() => setShowOrganizationSettingsModal(false)}
          onUpdateSettings={handleUpdateOrganizationSettings}
          isLoading={organizationSettingsLoading}
        />
      )}

      {/* Delete Group Modal */}
      {showDeleteModal && (
        <DeleteGroupModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDeleteGroup}
          groupName={selectedGroup?.name}
        />
      )}

      {/* Generic Success Modal */}
      <Modal type={"success"} show={showSuccessModal} onClose={() => setShowSuccessModal(false)} header={successModalHeaderAndMessage.header} buttons={false}>
        <div>{successModalHeaderAndMessage.message}</div>
      </Modal>

      {/* Generic Failure Modal */}
      <Modal type={"error"} show={showErrorModal} onClose={() => setShowErrorModal(false)} header={errorModalHeaderAndMessage.header} buttons={false}>
        <div>{errorModalHeaderAndMessage.message}</div>
      </Modal>
    </div>
  );
};

export default WhatsAppGroupsPage;
